import { isTooManyRequestsError, rateLimitRetryOptions } from '@restDataAccess/rateLimit';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { fetchAuthApiGet } from '../fetchAuthApiGet.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type singleFetchProps<T> = {
  baseApiRoute: ApiNameType;
  route: string;
  cacheKeys: (string | number | null | undefined)[];
  headers?: HeadersInit;
  returnJsonResult?: boolean;
  refetchOnWindowFocus?: boolean;
  enabled?: boolean;
  refetchInterval?: UseQueryOptions<T>['refetchInterval'];
  staleTime?: number;
  identifier?: string;
  /** Overrides the default retry policy, including the endless retry on HTTP 429. */
  retry?: UseQueryOptions<T>['retry'];
};

/**
 * Authenticated GET with TanStack Query.
 * Resolves the API base URL, adds `authorizationHeader`, and caches the result
 * under `cacheKeys` plus the resolved `api` base URL and `route`.
 *
 * HTTP 429 is retried indefinitely with a random 1–10s delay (see `rateLimitRetryOptions`);
 * all other failures keep the default three attempts. While those retries are in flight the
 * returned `isRateLimited` is true, which callers can surface as a "data delayed" notice.
 *
 * @typeParam T - JSON payload type.
 * @param baseApiRoute - API name (e.g., API_NAMES.servicesApi).
 * @param route - Endpoint path appended to the base URL.
 * @param cacheKeys - Unique cache key for the query.
 * @param headers - Extra request headers to merge.
 * @param refetchOnWindowFocus - Refetch on window focus (default: false).
 * @param enabled - Whether the query is enabled (default: true).
 * @param returnJsonResult - Whether to return the JSON result directly (default: false).
 * @param refetchInterval - Interval in milliseconds to refetch the data, or a function of the
 *   query returning one (`false` stops polling).
 * @param staleTime - Time in milliseconds before cached data is considered stale.
 * @param retry - Replaces the retry policy above when set.
 * @param identifier
 * @returns TanStack Query result plus `isRateLimited`.
 */
export const useAuthApiGetSingleFetch = <T>({
  baseApiRoute,
  route,
  cacheKeys,
  headers,
  refetchOnWindowFocus = false,
  enabled = true,
  returnJsonResult,
  refetchInterval,
  staleTime,
  identifier = 'object',
  retry,
}: singleFetchProps<T>) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);

  const getData = async (): Promise<T> => {
    try {
      return await fetchAuthApiGet<T>({
        api,
        route,
        user: auth.user,
        headers,
        identifier,
        returnJsonResult,
      });
    } catch (error) {
      console.error(`Failed to fetch`, error);
      throw error;
    }
  };
  const queryKey = cacheKeys.filter(
    (key): key is string | number => key !== null && key !== undefined
  );

  const query = useQuery<T>({
    ...rateLimitRetryOptions,
    ...(retry !== undefined && { retry }),
    queryKey: [...queryKey, api, route],
    queryFn: getData,
    refetchOnWindowFocus,
    refetchInterval,
    staleTime,
    enabled: enabled && api != null,
  });

  return { ...query, isRateLimited: isTooManyRequestsError(query.failureReason) };
};
